import React, { useState } from 'react';
import { Button, Card, Space, Tag, Typography, message, Modal, Image } from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Api from '../../../Network';

const { Text } = Typography;

type RegistrationDoc = {
  type: string;
  available: boolean;
  url: string;
  filename?: string | null;
  mime?: string | null;
  extension?: string | null;
};

interface CustomerRegistrationFileProps {
  customer: any;
  documents?: RegistrationDoc[];
  onUpdated?: (customerId?: number) => void;
}

const DOC_LABELS: Record<string, { ar: string; en: string }> = {
  commercial_registration: { ar: 'السجل التجاري', en: 'Commercial registration' },
  tax_certificate: { ar: 'شهادة ضريبة القيمة المضافة', en: 'VAT certificate' },
  national_address: { ar: 'العنوان الوطني', en: 'National address' },
  signature: { ar: 'التوقيع', en: 'Signature' },
  stamp: { ar: 'الختم', en: 'Stamp' },
  agreement: { ar: 'الاتفاقية PDF', en: 'Agreement PDF' },
};

const MIME_EXT: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'application/pdf': 'pdf',
};

function apiBaseUrl(): string {
  let baseURL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000').trim();
  if (baseURL && !/^https?:\/\//.test(baseURL)) {
    baseURL = `http://${baseURL}`;
  }
  return baseURL.replace(/\/api$/i, '').replace(/\/+$/, '');
}

function parseFilename(disposition?: string): string | null {
  if (!disposition) return null;
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim());
    } catch {
      return utfMatch[1].trim();
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(disposition);
  return plainMatch?.[1]?.trim() || null;
}

function extensionFrom(name?: string | null, mime?: string | null, fallbackType?: string): string {
  if (name && name.includes('.')) {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext && ext !== 'bin' && ext !== 'file') return ext;
  }
  if (mime && MIME_EXT[mime.split(';')[0].trim()]) {
    return MIME_EXT[mime.split(';')[0].trim()];
  }
  if (fallbackType === 'agreement') return 'pdf';
  if (fallbackType === 'signature' || fallbackType === 'stamp') return 'png';
  return 'bin';
}

const CustomerRegistrationFile: React.FC<CustomerRegistrationFileProps> = ({
  customer,
  documents = [],
  onUpdated,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [approving, setApproving] = useState(false);
  const [loadingType, setLoadingType] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; title: string; isImage: boolean } | null>(
    null
  );
  const isAr = i18n.language?.startsWith('ar');
  const needsQoyod = customer?.status === 'Pending' || !customer?.qoyod_id;

  const labelFor = (type: string) => {
    const row = DOC_LABELS[type];
    if (!row) return type;
    return isAr ? row.ar : row.en;
  };

  const fetchDocumentBlob = async (doc: RegistrationDoc) => {
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${apiBaseUrl()}/api/admin/customers/${customer.id}/documents/${doc.type}`,
      {
        responseType: 'blob',
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      }
    );

    const headerMime = String(response.headers['content-type'] || '')
      .split(';')[0]
      .trim();
    const mime =
      headerMime && headerMime !== 'application/octet-stream'
        ? headerMime
        : doc.mime || headerMime || 'application/octet-stream';

    // API errors often come back as JSON blobs with 4xx/5xx
    if (mime.includes('application/json') || response.status >= 400) {
      let errorMsg = t('customers.registration.downloadError', 'Failed to download file');
      try {
        const text = await (response.data as Blob).text();
        const json = JSON.parse(text);
        errorMsg = json?.msg || json?.message || errorMsg;
      } catch {
        // keep default
      }
      throw new Error(errorMsg);
    }

    const disposition = response.headers['content-disposition'] as string | undefined;
    const filename =
      parseFilename(disposition) ||
      doc.filename ||
      `${customer.name || 'customer'}-${doc.type}.${extensionFrom(doc.filename, mime, doc.type)}`;

    const blob = new Blob([response.data], { type: mime });
    return { blob, mime, filename };
  };

  const openDocument = async (doc: RegistrationDoc) => {
    if (!doc.available) return;
    setLoadingType(doc.type);
    try {
      const { blob, mime, filename } = await fetchDocumentBlob(doc);
      const url = globalThis.URL.createObjectURL(blob);
      const isImage = mime.startsWith('image/');
      const isPdf = mime === 'application/pdf' || filename.toLowerCase().endsWith('.pdf');

      if (isImage) {
        setPreview({ url, title: `${labelFor(doc.type)} — ${filename}`, isImage: true });
        return;
      }

      if (isPdf) {
        const win = globalThis.open(url, '_blank');
        if (!win) {
          // popup blocked → force download
          triggerDownload(url, filename);
        } else {
          setTimeout(() => globalThis.URL.revokeObjectURL(url), 60_000);
        }
        return;
      }

      triggerDownload(url, filename);
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 30_000);
    } catch (error: any) {
      message.error(
        error?.message || t('customers.registration.downloadError', 'Failed to download file')
      );
    } finally {
      setLoadingType(null);
    }
  };

  const downloadDocument = async (doc: RegistrationDoc) => {
    if (!doc.available) return;
    setLoadingType(doc.type);
    try {
      const { blob, filename } = await fetchDocumentBlob(doc);
      const url = globalThis.URL.createObjectURL(blob);
      triggerDownload(url, filename);
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 30_000);
    } catch (error: any) {
      message.error(
        error?.message || t('customers.registration.downloadError', 'Failed to download file')
      );
    } finally {
      setLoadingType(null);
    }
  };

  const triggerDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const approve = () => {
    Modal.confirm({
      title: t('customers.registration.confirmTitle', 'Confirm and send to Qoyod'),
      content: t(
        'customers.registration.confirmBody',
        'Review the customer file, then send the account to Qoyod. Status stays Pending if Qoyod rejects the data.'
      ),
      okText: t('customers.registration.confirmOk', 'Confirm and send'),
      cancelText: t('common.no', 'No'),
      onOk: async () => {
        setApproving(true);
        try {
          const response: any = await Api.post(`/api/admin/customers/${customer.id}/approve-qoyod`);
          message.success(
            t('customers.registration.confirmSuccess', 'Customer confirmed and sent to Qoyod')
          );
          const syncedCustomer = response?.data?.customer ?? response?.customer;
          const newId = syncedCustomer?.id;
          if (newId && Number(newId) !== Number(customer.id)) {
            history.replace(`/customers/${newId}`);
            return;
          }
          onUpdated?.(newId);
        } catch (error: any) {
          const msg =
            error?.response?.data?.msg ||
            error?.response?.data?.message ||
            t('customers.registration.confirmError', 'Qoyod sync failed');
          message.error(msg);
        } finally {
          setApproving(false);
        }
      },
    });
  };

  const address = customer?.billing_address;
  const addressText = address
    ? [
        address.building_number,
        address.billing_address,
        address.billing_district || address.billing_state,
        address.billing_city,
        address.billing_zip,
        address.additional_number,
        address.billing_country,
      ]
        .filter(Boolean)
        .join('، ')
    : '-';

  return (
    <Card
      className="info-card"
      style={{ marginBottom: 24 }}
      title={
        <Space>
          <FileTextOutlined />
          {t('customers.registration.title', 'Registration file')}
        </Space>
      }
      extra={
        needsQoyod ? (
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={approving}
            onClick={approve}
          >
            {t('customers.registration.confirmOk', 'Confirm and send to Qoyod')}
          </Button>
        ) : (
          <Tag color="green">
            {t('customers.registration.synced', 'Synced to Qoyod')} #{customer.qoyod_id}
          </Tag>
        )
      }
    >
      <div className="registration-grid">
        <div>
          <Text type="secondary">{t('customers.details.manager', 'Manager')}</Text>
          <div>{customer.manager_name || '-'}</div>
        </div>
        <div>
          <Text type="secondary">{t('customers.details.email', 'Email')}</Text>
          <div>{customer.email || '-'}</div>
        </div>
        <div>
          <Text type="secondary">{t('customers.details.crNumber', 'CR number')}</Text>
          <div>{customer.commercial_registration_number || '-'}</div>
        </div>
        <div>
          <Text type="secondary">{t('customers.details.signer', 'Signer')}</Text>
          <div>
            {customer.signer_name || '-'}
            {customer.signer_title ? ` — ${customer.signer_title}` : ''}
          </div>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Text type="secondary">{t('customers.details.nationalAddress', 'National address')}</Text>
          <div>{addressText}</div>
        </div>
      </div>

      <Space wrap style={{ marginTop: 16 }}>
        {documents.map((doc) => (
          <Space.Compact key={doc.type}>
            <Button
              icon={<EyeOutlined />}
              disabled={!doc.available}
              loading={loadingType === doc.type}
              onClick={() => openDocument(doc)}
            >
              {labelFor(doc.type)}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              disabled={!doc.available}
              loading={loadingType === doc.type}
              onClick={() => downloadDocument(doc)}
            />
          </Space.Compact>
        ))}
      </Space>

      <Modal
        open={!!preview}
        title={preview?.title}
        footer={null}
        onCancel={() => {
          if (preview?.url) globalThis.URL.revokeObjectURL(preview.url);
          setPreview(null);
        }}
        width={720}
        destroyOnClose
      >
        {preview?.isImage ? (
          <div style={{ textAlign: 'center' }}>
            <Image src={preview.url} alt={preview.title} style={{ maxHeight: '70vh' }} />
          </div>
        ) : null}
      </Modal>
    </Card>
  );
};

export default CustomerRegistrationFile;
